import React, { useEffect, useMemo, useState } from "react";
import { tableHeaderCellSx } from "./components/DataCard";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  IconButton,
  Tooltip,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Alert,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard from "./components/DataCard";
import apiClient from "./utils/apiClient";
import Footer from "./Footer";
import PasswordField from "./PasswordField";
import { useRoleStore } from "./store/roleStore";
import { sortRoles } from "./utils/roles";
import { useUsersStore } from "./store/usersStore";
import { useAuthStore } from "./store/authStore";

export default function Users() {
  const { users, isLoading, error, fetchUsers, updateUser, deleteUser, addUser, resetTotp } = useUsersStore();
  const { roles, fetchRoles } = useRoleStore();
  const sortedRoles = useMemo(() => sortRoles(roles), [roles]);
  // Only Admin can mutate user rows (backend gates user_api's writable methods
  // to Admin via method_roles). InternalUser reaches this page for read-only
  // access — pick a user to review — so hide every write affordance for
  // non-Admin callers to avoid inviting a 403 they can't act on.
  const currentUserRoles = useAuthStore((state) => state.user?.roles) || [];
  const isAdmin = Array.isArray(currentUserRoles)
    ? currentUserRoles.includes("Admin")
    : currentUserRoles === "Admin";
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isResetTotpDialogOpen, setIsResetTotpDialogOpen] = useState(false);
  const [resetTotpStatus, setResetTotpStatus] = useState(null);

  const [newUser, setNewUser] = useState({
    email: "",
    fullname: "",
    roles: [],
    password: "",
    confirmPassword: "",
  });
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    if (roles.length === 0) {
      fetchRoles();
    }
  }, [roles.length, fetchRoles]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAddUser = () => {
    setNewUser({ email: "", fullname: "", roles: [], password: "", confirmPassword: "" });
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setNewUser({ email: "", fullname: "", roles: [], password: "", confirmPassword: "" });
    setIsAddDialogOpen(false);
  };

  const handleSaveAdd = async () => {
    if (newUser.password !== newUser.confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    try {
      // Sent as plaintext (over HTTPS) rather than client-hashed — the
      // backend hashes it server-side with the real scrypt KDF. Sending a
      // bare client-computed SHA-256 digest let *that* digest become the
      // actual stored credential (unsalted, GPU-crackable).
      const userToSubmit = { ...newUser };
      delete userToSubmit.confirmPassword;
      userToSubmit.id = userToSubmit.email;

      await addUser(userToSubmit);
      handleCloseAddDialog();
      // No fetchUsers here; Zustand will update the array
    } catch (error) {
      console.error("Error adding user:", error);
    }
  };

  const handleNewUserFieldChange = (field, value) => {
    setNewUser((prevUser) => ({
      ...prevUser,
      [field]: value,
    }));
  };

  const handleEditUser = (user) => {
    setEditingUser({
      ...user,
      password: "",
      confirmPassword: "",
    });
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditingUser(null);
    setIsEditDialogOpen(false);
    setResetTotpStatus(null);
  };

  const handleOpenDeleteDialog = (userId) => {
    setDeleteUserId(userId);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteUserId(null);
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(deleteUserId);
      handleCloseDeleteDialog();
      // No fetchUsers here; Zustand will update the array
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const updatedUser = { ...editingUser };

      if (updatedUser.password || updatedUser.confirmPassword) {
        if (updatedUser.password !== updatedUser.confirmPassword) {
          alert("Passwords do not match");
          return;
        }
        // Sent as plaintext (over HTTPS) rather than client-hashed — the
        // backend hashes it server-side with the real scrypt KDF. Sending a
        // bare client-computed SHA-256 digest let *that* digest become the
        // actual stored credential (unsalted, GPU-crackable).
      } else {
        delete updatedUser.password;
      }

      delete updatedUser.confirmPassword;

      await updateUser(updatedUser.id, updatedUser);
      handleCloseEditDialog();
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleEditFieldChange = (field, value) => {
    setEditingUser((prevUser) => ({
      ...prevUser,
      [field]: value,
    }));
  };

  const handleOpenResetTotpDialog = () => {
    setResetTotpStatus(null);
    setIsResetTotpDialogOpen(true);
  };

  const handleCloseResetTotpDialog = () => {
    setIsResetTotpDialogOpen(false);
  };

  const handleConfirmResetTotp = async () => {
    if (!editingUser?.id) return;
    try {
      await resetTotp(editingUser.id);
      setEditingUser((prev) =>
        prev ? { ...prev, is_mfa_enabled: false, totp_secret: null } : prev
      );
      setResetTotpStatus({
        severity: "success",
        message: `TOTP reset for ${editingUser.id}. They will re-enroll on next login.`,
      });
    } catch (e) {
      setResetTotpStatus({
        severity: "error",
        message: e?.message || "Failed to reset TOTP",
      });
    } finally {
      setIsResetTotpDialogOpen(false);
    }
  };

  const handleRoleChange = async (userId, newRoles) => {
    try {
      await updateUser(userId, { roles: newRoles });
      // UI will auto-update from Zustand store
    } catch (e) {
      console.error("Failed to update user roles:", e);
    }
  };

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<PersonIcon />}
          title="Users"
          subtitle="Manage user accounts and role assignments"
          action={
            isAdmin ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddUser}
              >
                Add User
              </Button>
            ) : null
          }
        />
        <DataCard noPadding>
          {isLoading && (
            <Box sx={{ p: 3, color: "text.secondary" }}>Loading users…</Box>
          )}
          {error && (
            <Box sx={{ p: 3, color: "error.main" }}>Error: {error}</Box>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderCellSx} />
                <TableCell sx={tableHeaderCellSx}>Email</TableCell>
                <TableCell sx={tableHeaderCellSx}>Reports</TableCell>
                <TableCell sx={tableHeaderCellSx}>Roles</TableCell>
                <TableCell sx={tableHeaderCellSx} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users &&
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={{ width: 56 }}>
                      <Link
                        to={`/user/${user.id}`}
                        style={{ textDecoration: "none", color: "#1976d2" }}
                      >
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <PersonIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/user/${user.id}`}
                        style={{ textDecoration: "none", color: "#1976d2" }}
                      >
                        {user.id}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link to={`/report-list/${user.id}`}>reports</Link>
                    </TableCell>
                    <TableCell>{user?.roles?.join(", ")}</TableCell>
                    <TableCell align="right">
                      {isAdmin ? (
                        <>
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditUser(user)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete User">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(user.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </DataCard>
      </PageContainer>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete User?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user with ID {deleteUserId}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="secondary" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <form autoComplete="off">
            <TextField
              label="Email"
              value={editingUser?.email || ""}
              fullWidth
              margin="normal"
              disabled
              autoComplete="off"
            />
            <TextField
              label="Full Name"
              value={editingUser?.fullname || ""}
              fullWidth
              margin="normal"
              onChange={(e) =>
                handleEditFieldChange("fullname", e.target.value)
              }
              autoComplete="off"
            />
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Checkbox
                checked={editingUser?.editPassword || false}
                onChange={(_, checked) =>
                  handleEditFieldChange("editPassword", checked)
                }
                color="primary"
                id="edit-password-toggle"
              />
              <label htmlFor="edit-password-toggle" style={{ cursor: "pointer" }}>
                Edit Password
              </label>
            </Box>
            <PasswordField
              value={editingUser?.password || ""}
              onChange={(value) => handleEditFieldChange("password", value)}
              confirmValue={editingUser?.confirmPassword || ""}
              confirmOnChange={(value) =>
                handleEditFieldChange("confirmPassword", value)
              }
              autoComplete="new-password"
              disabled={!editingUser?.editPassword}
            />
            <Select
              label="Roles"
              multiple
              value={editingUser?.roles || []}
              onChange={(e) => handleEditFieldChange("roles", e.target.value)}
              fullWidth
              renderValue={(selected) => selected.join(", ")}
            >
              {sortedRoles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox
                      checked={editingUser?.roles?.includes(role.id)}
                    />
                    <ListItemText primary={role.id} />
                  </MenuItem>
                ))}
            </Select>
            <Box
              mt={2}
              p={1.5}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={editingUser?.report_notification_emails !== false}
                    onChange={(e) =>
                      handleEditFieldChange(
                        "report_notification_emails",
                        e.target.checked
                      )
                    }
                  />
                }
                label="Email user when a report finishes"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Sent when an analysis this user started completes or errors out,
                with a link straight to the report.
              </Typography>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              mt={2}
              p={1.5}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
            >
              <Box>
                <Box sx={{ fontWeight: 500 }}>Two-Factor Auth (TOTP)</Box>
                <Box sx={{ color: "text.secondary", fontSize: 13 }}>
                  {editingUser?.is_mfa_enabled
                    ? "Enrolled. Resetting will clear their TOTP secret and require re-enrollment on next login."
                    : "Not configured."}
                </Box>
              </Box>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                disabled={!editingUser?.is_mfa_enabled}
                onClick={handleOpenResetTotpDialog}
              >
                Reset TOTP
              </Button>
            </Box>
            {resetTotpStatus && (
              <Alert
                severity={resetTotpStatus.severity}
                sx={{ mt: 2 }}
                onClose={() => setResetTotpStatus(null)}
              >
                {resetTotpStatus.message}
              </Alert>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} color="secondary" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset TOTP Confirm Dialog */}
      <Dialog open={isResetTotpDialogOpen} onClose={handleCloseResetTotpDialog}>
        <DialogTitle>Reset TOTP?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Reset TOTP for <strong>{editingUser?.id}</strong>? Their existing
            authenticator will stop working and they will need to re-enroll on
            next login.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetTotpDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmResetTotp} color="secondary" autoFocus>
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onClose={handleCloseAddDialog}>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            value={newUser.email}
            fullWidth
            margin="normal"
            onChange={(e) => handleNewUserFieldChange("email", e.target.value)}
            error={
              newUser.email !== "" &&
              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email)
            }
            helperText={
              newUser.email !== "" &&
              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email)
                ? "Please enter a valid email address"
                : ""
            }
            required
          />
          <TextField
            label="Full Name"
            value={newUser.fullname}
            fullWidth
            margin="normal"
            onChange={(e) =>
              handleNewUserFieldChange("fullname", e.target.value)
            }
          />
          <PasswordField
            value={newUser.password}
            onChange={(value) => handleNewUserFieldChange("password", value)}
            confirmValue={newUser.confirmPassword}
            confirmOnChange={(value) =>
              handleNewUserFieldChange("confirmPassword", value)
            }
          />
          <Select
            label="Roles"
            multiple
            value={newUser.roles}
            onChange={(e) => handleNewUserFieldChange("roles", e.target.value)}
            fullWidth
            renderValue={(selected) => selected.join(", ")}
          >
            {sortedRoles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  <Checkbox checked={newUser.roles.includes(role.id)} />
                  <ListItemText primary={role.id} />
                </MenuItem>
              ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveAdd} color="secondary" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
